import { createSlice, PayloadAction } from '@reduxjs/toolkit'
// @ts-expect-error uuid v3 uses subpath imports
import uuidv4 from 'uuid/v4'
import cloneDeep from 'lodash/cloneDeep'
import first from 'lodash/first'

/**
 * An item can be a song or a release
 * The only identifier for either release or song is its uuid which is inserted when the item was added to the queue.
 * When a song is added to the history, another historyUuid will be added such that it is unique in the history list.
 */

interface QueueItem {
  uuid?: string
  releaseid?: string | number
  trackid?: string | number
  playlistid?: string | number
  historyUuid?: string
  songs?: QueueItem[]
  flagged?: boolean
  assettype?: string
  [key: string]: unknown
}

interface PlayQueueState {
  history: QueueItem[]
  items: QueueItem[]
  selectedItem: QueueItem | null
  selectedSong: QueueItem | null
}

const initialState: PlayQueueState = {
  history: [],
  items: [],
  selectedItem: null,
  selectedSong: null,
}

// --- Helper functions (exported for use in selectors/components) ---

const findItemIndexFromArray = (selectedItem: QueueItem | null, items: QueueItem[] | null | undefined): number => {
  if (!items) return -1
  return items.findIndex((item) => item.uuid === (selectedItem && selectedItem.uuid))
}

const findSongIndexFromHistory = (song: QueueItem | null, history: QueueItem[]): number => {
  return history.findIndex((item) => item.historyUuid === (song && song.historyUuid))
}

export const isRelease = (item: QueueItem): boolean => {
  return item.releaseid !== undefined && item.trackid === undefined
}

export const isPlaylist = (item: QueueItem | null): boolean => {
  return item != null && item.playlistid !== undefined && item.trackid === undefined
}

export const isSong = (item: QueueItem): boolean => {
  return item.trackid !== undefined
}

export const isReleaseFlagged = (item: QueueItem): boolean => {
  if (!item.songs || item.songs.length === 0) return false
  return item.songs.some((song) => song.flagged)
}

export const getFirstSongFromItem = (item: QueueItem): QueueItem | null => {
  if (isRelease(item) || isPlaylist(item)) {
    return item.songs?.[0] || null
  }
  return isSong(item) ? item : null
}

const getItemFromUuid = (uuid: string | undefined, items: QueueItem[]): QueueItem | null => {
  if (!uuid) return null
  let result: QueueItem | null = null
  items.forEach((item) => {
    if (item.uuid === uuid) {
      result = item
    } else if (isRelease(item) || isPlaylist(item)) {
      item.songs?.forEach((track) => {
        if (track.uuid === uuid) {
          result = item
        }
      })
    }
  })
  return result
}

const deleteItemFromItems = (item: QueueItem, items: QueueItem[]): QueueItem[] => {
  return items
    .filter((tmpItem) => item.uuid !== tmpItem.uuid)
    .map((tmpItem2) => {
      if (isRelease(tmpItem2) || isPlaylist(tmpItem2)) {
        return {
          ...tmpItem2,
          songs: tmpItem2.songs?.filter((track) =>
            item.uuid ? track.uuid !== item.uuid : track.trackid !== item.trackid
          ),
        }
      }
      return tmpItem2
    })
}

const deleteItemFromHistory = (item: QueueItem, history: QueueItem[]): QueueItem[] => {
  return history.filter((song) => {
    const isSameUuid = item.uuid && song.uuid === item.uuid
    const isSameTrackid = !item.uuid && song.trackid === item.trackid
    const isSongInItem = item.songs?.some((songItem) => songItem.uuid === song.uuid)
    return !(isSameUuid || isSameTrackid || isSongInItem)
  })
}

const deleteSongFromSelectedItem = (song: QueueItem, selectedItem: QueueItem): QueueItem => {
  return {
    ...selectedItem,
    songs: selectedItem.songs?.filter((selectedItemSong) =>
      song.uuid ? song.uuid !== selectedItemSong.uuid : song.trackid !== selectedItemSong.trackid
    ),
  }
}

export const getNextItemFromItems = (currentItem: QueueItem | null, items: QueueItem[]): QueueItem | null => {
  const currentIndex = findItemIndexFromArray(currentItem, items)
  if (currentIndex >= 0 && currentIndex < items.length - 1) {
    return items[currentIndex + 1]
  }
  return null
}

const addUuid = (item: QueueItem): QueueItem => {
  const result = cloneDeep(item)
  result.uuid = uuidv4()
  if (isRelease(result) || isPlaylist(result)) {
    result.songs = result.songs?.map((track: QueueItem) => {
      const newTrack = cloneDeep(track)
      newTrack.uuid = uuidv4()
      return newTrack
    })
  }
  return result
}

const pushSongToHistory = (song: QueueItem, history: QueueItem[]): QueueItem[] => {
  const result = cloneDeep(history)
  const newObject = { ...song, historyUuid: uuidv4() }
  result.push(newObject)
  return result
}

export const getNextItemAndSong = (state: PlayQueueState): { item: QueueItem | null; song: QueueItem | null } => {
  const { items, selectedItem, selectedSong } = state

  if (!selectedItem || items.length === 0) {
    return { item: null, song: null }
  }

  if (isRelease(selectedItem) || isPlaylist(selectedItem)) {
    const currentIndex = findItemIndexFromArray(selectedSong, selectedItem.songs)
    if (currentIndex >= 0 && selectedItem.songs && currentIndex < selectedItem.songs.length - 1) {
      const nextSong = selectedItem.songs[currentIndex + 1]
      return { item: selectedItem, song: nextSong }
    }
  }

  const currentItemIndex = findItemIndexFromArray(selectedItem, items)
  if (currentItemIndex >= 0 && currentItemIndex < items.length - 1) {
    const nextItem = items[currentItemIndex + 1]
    const firstSong = getFirstSongFromItem(nextItem)
    return { item: nextItem, song: firstSong }
  }

  return { item: null, song: null }
}

export const getPreviousItemAndSong = (state: PlayQueueState): { item: QueueItem | null; song: QueueItem | null } => {
  const { items, history } = state

  if (history.length === 0) {
    return { item: null, song: null }
  }

  const previousSong = history[history.length - 1]
  const previousItem = getItemFromUuid(previousSong.uuid, items)

  return { item: previousItem, song: previousSong }
}

const itemsToBeDisplayed = ({ items, selectedItem }: { items: QueueItem[]; selectedItem: QueueItem | null }): QueueItem[] => {
  if (!items) return []
  const index = findItemIndexFromArray(selectedItem, items)
  return index !== -1 ? items.slice(index) : items
}

// --- Slice ---

const playQueueSlice = createSlice({
  name: 'playQueue',
  initialState,
  reducers: {
    insertItemAtTop: (state, action: PayloadAction<QueueItem>) => {
      const newItem = addUuid(action.payload)
      const { items, history, selectedSong } = state

      if (newItem?.songs && newItem.songs.length > 0) {
        newItem.songs = newItem.songs.filter((s) => s.assettype === 'audio')
        const firstSong = getFirstSongFromItem(newItem)
        if (selectedSong && firstSong && selectedSong.uuid === firstSong.uuid) {
          return state
        }
        const newItems = [...newItem.songs]
        const itemsUpToNext = itemsToBeDisplayed({ items: newItems, selectedItem: newItem })
        return {
          ...state,
          items: itemsUpToNext,
          selectedItem: newItem,
          selectedSong: getFirstSongFromItem(newItem),
        }
      }

      const itemsWithoutLastPlayedTrack = items.filter(
        (item) => item.uuid !== selectedSong?.uuid
      )
      const newItems = [newItem, ...itemsWithoutLastPlayedTrack]
      const newHistory = selectedSong ? pushSongToHistory(selectedSong, history) : history
      const itemsUpToNext = itemsToBeDisplayed({ items: newItems, selectedItem: newItem })

      return {
        ...state,
        items: itemsUpToNext,
        history: newHistory,
        selectedItem: newItem,
        selectedSong: newItem,
      }
    },

    insertItemNext: (state, action: PayloadAction<QueueItem>) => {
      const newItem = addUuid(action.payload)
      const { items, selectedItem } = state

      if (newItem?.songs && newItem.songs.length > 0) {
        newItem.songs = newItem.songs.filter((s) => s.assettype === 'audio')
        const newItems = [...newItem.songs, ...items]
        const itemsUpToNext = itemsToBeDisplayed({ items: newItems, selectedItem: newItem })
        return { ...state, items: itemsUpToNext, selectedItem: newItem }
      }

      const index = findItemIndexFromArray(selectedItem, items)
      let newItems: QueueItem[]
      if (index >= 0) {
        newItems = [...items.slice(0, index + 1), newItem, ...items.slice(index + 1)]
      } else {
        newItems = [newItem, ...items]
      }
      return { ...state, items: itemsToBeDisplayed({ items: newItems, selectedItem }) }
    },

    insertItemAtBottom: (state, action: PayloadAction<QueueItem>) => {
      const newItem = addUuid(action.payload)
      const { items, selectedItem } = state

      if (newItem?.songs && newItem.songs.length > 0) {
        newItem.songs = newItem.songs.filter((s) => s.assettype === 'audio')
        const newItems = [...items, ...newItem.songs]
        const itemsUpToNext = itemsToBeDisplayed({ items: newItems, selectedItem: newItem })
        return { ...state, items: itemsUpToNext, selectedItem: newItem }
      }

      const index = findItemIndexFromArray(selectedItem, items)
      let newItems: QueueItem[]
      if (index >= 0) {
        newItems = [...items.slice(0, items.length), newItem, ...items.slice(items.length)]
      } else {
        newItems = [newItem, ...items]
      }
      return { ...state, items: itemsToBeDisplayed({ items: newItems, selectedItem }) }
    },

    seekNext: (state) => {
      const { history, selectedSong, items } = state
      const { item, song } = getNextItemAndSong(state)

      if (item !== null && song !== null) {
        const newHistory = selectedSong !== null
          ? pushSongToHistory(selectedSong, history)
          : history
        const updatedItems = items.filter((upNextItem) => upNextItem.uuid !== selectedSong?.uuid)

        return {
          ...state,
          selectedItem: item,
          selectedSong: song,
          history: newHistory,
          items: updatedItems,
        }
      }
      return state
    },

    seekPrevious: (state) => {
      const { items, history } = state
      const { song } = getPreviousItemAndSong(state)

      if (song) {
        const newHistory = history.slice(0, history.length - 1)
        const updatedItems = [
          song,
          ...items.filter((upNextItem) => upNextItem.trackid !== song.trackid),
        ]
        return { ...state, selectedSong: song, history: newHistory, items: updatedItems }
      }
      return state
    },

    seekToItem: (state, action: PayloadAction<QueueItem>) => {
      const item = action.payload
      const { history, items, selectedSong } = state
      const newHistory = selectedSong !== null
        ? pushSongToHistory(selectedSong, history)
        : history
      const selectedItem = getItemFromUuid(item?.uuid, items)

      return { ...state, history: newHistory, selectedItem, selectedSong: item, items }
    },

    deleteItem: (state, action: PayloadAction<QueueItem>) => {
      const item = action.payload
      const { items, history, selectedItem, selectedSong } = state
      return {
        ...state,
        items: deleteItemFromItems(item, items),
        history: deleteItemFromHistory(item, history),
        selectedSong: item?.uuid === selectedSong?.uuid ? {} as QueueItem : selectedSong,
        selectedItem:
          isSong(item) && selectedItem && isPlaylist(selectedItem)
            ? deleteSongFromSelectedItem(item, selectedItem)
            : selectedItem,
      }
    },

    setPlaylist: (state, action: PayloadAction<QueueItem>) => {
      const newItem = addUuid(action.payload)
      let newSong: QueueItem | null = null
      if (newItem.songs && newItem.songs.length > 0) {
        newSong = first(newItem.songs) || null
      }
      const newItems = newItem.songs || []
      const itemsUpToNext = itemsToBeDisplayed({ items: newItems, selectedItem: newItem })

      return {
        ...state,
        history: [],
        items: itemsUpToNext,
        selectedItem: newItem,
        selectedSong: newSong,
      }
    },

    resetPlayQueue: () => initialState,
    reset: () => initialState,
    clearQueue: () => initialState,
  },
})

export const playQueueActions = playQueueSlice.actions
export const playQueueReducer = playQueueSlice.reducer
export default playQueueActions
